<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:link',
	name: '$t:blocks.input_list_m2m.name',
	description: '$t:blocks.input_list_m2m.description',
	category: 'input',
	tags: ['form-control'],
	options: [
		{ name: 'fieldId', label: '$t:blocks.common.props.field' },
		{ name: 'referencedItemKey', label: '$t:blocks.common.props.referenced_item' },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'disabledNotice', label: '$t:blocks.common.props.disabled_notice' },
	],

	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { DefaultViewOptions } from '#layers/blocks/blocks/Listing.block.vue';
import type { RelatedDisplayItem, RelatedItemState } from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import type { UseM2MStateReturn } from '#layers/blocks/composables/relation/use-m2m-state';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { AnyItem, CompositePrimaryKey, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { FormSaveError } from '#layers/forms/public';
import type { ButtonProps } from '#layers/ui/public';
import { computed, ref, shallowRef, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOverlay } from '#imports';
import { omit, toStructuredError } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import RelatedItemListCard from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import { useM2MState } from '#layers/blocks/composables/relation/use-m2m-state';
import { isSameKey, stringifyPrimaryKey } from '#layers/data-model/public';
import { createdItemKey, injectFormBlockContext, isConnectedAsCreated, isCreatedItem, markCreatedItem, useDrawerFieldOverrides, useM2MPermissions, useRelationFormField } from '#layers/forms/public';
import { LazyCollectionDetailDrawer, LazyCollectionListingDrawer } from '#layers/listing/lazy';

type M2MActionProps = Partial<Omit<ButtonProps, 'onClick'>> & { onClick?: (m2mApi: UseM2MStateReturn) => void };

export interface InputListM2MBlockProps extends Pick<InputBlockProps, 'disabled' | 'disabledNotice' | 'readonly'> {
	fieldId: GlobalSingleRelationFieldId;
	referencedItemKey: CompositePrimaryKey;
	defaultView?: DefaultViewOptions;
	createAction?: M2MActionProps;
	editShown?: boolean;
	/** Pluralizable i18n key used for the item-count header (e.g. `common.n_files`). */
	countLabel?: string;
}

const props = withDefaults(defineProps<InputListM2MBlockProps>(), {
	editShown: true,
});

;

const { t } = useI18n();

const listCard = useTemplateRef('listCard');

const relationFieldContext = useRelationFormField<'to-many'>(props);
const { field, disabled, disabledNotice } = relationFieldContext;

const pageSize = ref(25);

const m2mState = useM2MState({
	...relationFieldContext,
	pageSize,
});
const {
	items,
	targetCollection,
	junctionCollection,
	m2mStructure,
	connectTarget,
	disconnectTarget,
	createTarget,
	undoCreateTarget,
	updateTarget,
	editConnectedTarget,
	saveTargetEdits,
	isNewTargetCreate,
	getTargetFromJunction,
	getTargetKeyFromJunction,
	getJunctionKey,
	connectedTargetsFilter,
	pageNumber,
	totalItemsCount,
	currentItemsCount,
	unpagedItemsCount,
	isLoading,
	isNewlyCreated,
	isNewlyConnected,
	isNewlyDisconnected,
	isNewlyUpdated,
	junctionPrimaryKeyFields,
} = m2mState;

const permissions = useM2MPermissions({ m2mStructure });

const fieldOverrides = useDrawerFieldOverrides(field, targetCollection);

const createActionProps = computed<Partial<ButtonProps>>(() => ({
	...omit(props.createAction ?? {}, ['onClick']),
}));

function getJunctionKeyString(junctionItem: AnyItem): string {
	const key = getJunctionKey(junctionItem);
	if (key && junctionCollection.value) return stringifyPrimaryKey(key);
	if (isCreatedItem(junctionItem)) return junctionItem[createdItemKey];
	return '';
}

function getItemState(junctionItem: AnyItem): RelatedItemState {
	if (isNewlyCreated(junctionItem)) return 'created';
	if (isConnectedAsCreated(junctionItem)) return 'created';
	if (isNewlyConnected(junctionItem)) return 'connected';
	if (isNewlyDisconnected(junctionItem)) return 'disconnected';
	if (isNewlyUpdated(junctionItem)) return 'updated';
	return 'default';
}

async function handleSelect() {
	if (!targetCollection.value) return;

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		props: {
			collectionId: targetCollection.value.id,
			selectionMode: 'multiple',
			filter: connectedTargetsFilter.value,
			defaultView: props.defaultView,
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result) {
		for (const targetKey of result.selection) {
			connectTarget(targetKey);
		}
	}
}

function handleRemove(targetData: AnyItem) {
	// Find the junction item from display items
	const junctionItem = items.value.find((item) => {
		const target = getTargetFromJunction(item);
		return target && isSameKey(target, targetData);
	});

	if (!junctionItem) return;

	if (isCreatedItem(junctionItem)) {
		undoCreateTarget(junctionItem);
	}
	else {
		const targetKey = getTargetKeyFromJunction(junctionItem);

		if (targetKey) {
			disconnectTarget(targetKey);
		}
	}
}

function handleUndo(targetData: AnyItem) {
	// Find the junction item from display items
	const junctionItem = items.value.find((item) => {
		const target = getTargetFromJunction(item);
		return target && isSameKey(target, targetData);
	});

	if (!junctionItem) return;

	const state = getItemState(junctionItem);
	const targetKey = getTargetKeyFromJunction(junctionItem);

	switch (state) {
		case 'created':
		case 'connected':
			undoCreateTarget(junctionItem);
			break;
		case 'disconnected':
			if (targetKey) {
				connectTarget(targetKey);
			}

			break;
	}
}

async function handleEdit(targetData: AnyItem) {
	if (!permissions.canEdit.value) return;
	if (!targetCollection.value) return;

	// Find the junction item from display items
	const junctionItem = items.value.find((item) => {
		const target = getTargetFromJunction(item);
		return target && isSameKey(target, targetData);
	});

	if (!junctionItem) return;

	const targetItem = getTargetFromJunction(junctionItem);
	const targetKey = getTargetKeyFromJunction(junctionItem);

	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		props: {
			collectionId: targetCollection.value.id,
			title: t('common.edit_item'),
			formMode: 'emit',
			itemEdits: targetItem ?? undefined,
			itemId: isNewTargetCreate(junctionItem) ? undefined : (targetKey ?? undefined),
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result as { data: AnyItem } | undefined;

	if (payload) {
		if (isNewTargetCreate(junctionItem)) {
			// New target: undo the pending create, then recreate with the edited data.
			// This replaces the pending junction item entirely.
			undoCreateTarget(junctionItem);
			createTarget(markCreatedItem(payload.data, junctionItem));
		}
		else if (isCreatedItem(junctionItem)) {
			if (targetKey) editConnectedTarget(targetKey, payload.data);
		}
		else if (targetKey) {
			updateTarget(targetKey, payload.data);
		}
	}
}

async function handleCreate() {
	if (props.createAction?.onClick) {
		props.createAction.onClick(m2mState);
		return;
	}

	if (!targetCollection.value) return;

	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		props: {
			collectionId: targetCollection.value.id,
			title: t('common.create_item'),
			formMode: 'emit',
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result as { data: AnyItem } | undefined;

	if (payload) {
		createTarget(payload.data);
		listCard.value?.scrollToBottom();
	}
}

// Compute display items with state
const displayItems = shallowRef<RelatedDisplayItem<AnyItem>[]>([]);

const formBlockContext = injectFormBlockContext();

// Persist edits to newly connected/uploaded targets after the parent (and its junction
// _connect) has saved, so a cancelled form never mutates the shared target record.
formBlockContext?.onSave(async (): Promise<FormSaveError[]> => {
	try {
		await saveTargetEdits();
		return [];
	}
	catch (error) {
		return [{ message: t('blocks.input_list_m2m.target_update_failed'), source: toStructuredError(error) }];
	}
});

function updateDisplayItems() {
	displayItems.value = items.value.map((junctionItem) => ({
		id: getJunctionKeyString(junctionItem),
		data: getTargetFromJunction(junctionItem) ?? {},
		state: getItemState(junctionItem),
	}));
}

watch(items, () => {
	const isFormLoading = formBlockContext?.state.value?.$loading ?? false;

	if (!isFormLoading && !isLoading.value) {
		updateDisplayItems();
	}
}, {
	deep: true,
	immediate: true,
});

watch(
	[() => isLoading.value, () => formBlockContext?.state.value?.$loading],
	([newLoading, newFormLoading], [oldLoading, oldFormLoading]) => {
		if (newLoading) return;

		if ((oldLoading && !newLoading) || (oldFormLoading && !newFormLoading)) {
			updateDisplayItems();
		}
	},
);
</script>

<template>
	<DisplayTemplateConfigurationError
		v-if="!targetCollection?.id"
		:error="$t('blocks.input_list_m2m.configuration_error')"
	/>
	<DisplayTemplateConfigurationError
		v-else-if="junctionPrimaryKeyFields?.length === 0"
		:error="$t('blocks.input_list_m2m.junction_missing_primary_key')"
	/>
	<RelatedItemListCard
		v-else
		ref="listCard"
		:items="displayItems"
		:count-label
		:collection-id="targetCollection?.id"
		:disabled
		:disabled-notice
		:readonly
		:aria-readonly="readonly"
		:show-edit="editShown"
		:create-action-props
		:pagination="{
			page: pageNumber,
			pageSize,
			totalItems: totalItemsCount,
			currentItems: currentItemsCount,
			unpagedItems: unpagedItemsCount,
		}"
		:loading="isLoading"
		:permissions="{
			canConnect: permissions.canConnect.value,
			canCreate: permissions.canCreate.value,
			canEdit: permissions.canEdit.value,
			canDisconnect: permissions.canDisconnect.value,
		}"
		show-create
		show-add-existing
		@select="handleSelect"
		@create="handleCreate"
		@edit="handleEdit"
		@remove="handleRemove"
		@undo="handleUndo"
		@update:page="pageNumber = $event"
		@update:page-size="pageSize = $event"
	/>
</template>
